import { useMemo, useState } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import CollapsibleHelpSection from '../common/CollapsibleHelpSection';
import { useRsNarrowBoardLayout } from '../../hooks/useRsCalendarStickyOffsets';
import styles from './ReservationBoardCalendar.module.css';

type RsBoardQuickGuideProps = {
  isBoardOwner: boolean;
};

export default function RsBoardQuickGuide({ isBoardOwner }: RsBoardQuickGuideProps) {
  const { t } = useTranslation();
  const isNarrow = useRsNarrowBoardLayout();
  const [sheetOpen, setSheetOpen] = useState(false);

  const stepsKey = useMemo(
    () => [
      'reservation.board.quick_guide.step_form',
      'reservation.board.quick_guide.step_calendar',
      'reservation.board.quick_guide.step_delete',
      ...(isBoardOwner ? ['reservation.board.quick_guide.step_share_owner'] : []),
      'reservation.board.quick_guide.step_multi',
    ],
    [isBoardOwner]
  );

  const helpBody = (
    <>
      <p className="mb-3 mt-0">
        {t('reservation.board.quick_guide.description', {
          defaultValue: 'このページの使い方を説明します。',
        })}
      </p>
      <ol className={`mb-3 ps-3 ${styles.helpGuideSteps}`}>
        {stepsKey.map((stepKey, index) => (
          <li key={stepKey} className="mb-1">
            {t(stepKey, { defaultValue: `ステップ${index + 1}` })}
          </li>
        ))}
      </ol>
      <div className={`alert alert-warning mb-0 ${styles.helpGuideNote}`}>
        <i className="fas fa-exclamation-triangle me-2" aria-hidden />
        <strong>{t('common.note', { defaultValue: '注意' })}:</strong>{' '}
        {t('reservation.board.quick_guide.delete_password_note', { defaultValue: '' })}
      </div>
    </>
  );

  const showLabel = t('reservation.board.quick_guide.show_again', {
    defaultValue: '使い方を表示',
  });
  const title = t('reservation.board.quick_guide.title', { defaultValue: '使い方' });

  if (isNarrow) {
    return (
      <div className="mb-0">
        <Button
          variant="link"
          size="sm"
          onClick={() => setSheetOpen(true)}
          className="px-0 py-0 text-decoration-none"
          aria-label={showLabel}
        >
          <i className="fas fa-question-circle me-1" aria-hidden />
          <span className="visually-hidden">{showLabel}</span>
        </Button>
        <Offcanvas
          show={sheetOpen}
          onHide={() => setSheetOpen(false)}
          placement="bottom"
          className={styles.helpOffcanvas}
          aria-labelledby="rs-board-help-title"
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title id="rs-board-help-title" className={styles.helpOffcanvasTitle}>
              <i className="fas fa-question-circle me-2" aria-hidden />
              {title}
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className={styles.helpOffcanvasBody}>{helpBody}</Offcanvas.Body>
        </Offcanvas>
      </div>
    );
  }

  return (
    <CollapsibleHelpSection
      title={title}
      storageKey="rs-board-calendar-v1"
      collapsedButtonKey="reservation.board.quick_guide.show_again"
      customContent={helpBody}
      dismissOnOutsideClick
      dismissOnScroll
    />
  );
}
